<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import FilteringDataDisplay from '../components/FilteringDataDisplay.vue'
import FormOrderItem from '../components/FormOrderItem.vue'
import TableCellRender from '../components/TableCellRender.vue'
import BannerOffline from '../components/BannerOffline.vue'
import { useNetwork } from '../composables/useNetwork'
import { useTimers } from '../store/timers'
import FormInventoryView from '../components/FormInventoryView.vue'
import { useInventory } from '../store/useInventory'
import { storeToRefs } from 'pinia'
import RenderProductLines from '../components/inventory/RenderProductLines.vue'
import RenderPartImage from '../components/inventory/RenderPartImage.vue'

//translations
const { t } = useI18n({ useScope: 'global' })
const tablesLabel = (key: string) => t(`tables.${key}`)

//modal logic
const tab = ref('balance')
const modal = ref(false)
const currentModalData = ref()
const startModal = (data: any) => {
  modal.value = true
  currentModalData.value = data
}
const startImageModal = (data: any) => {
  itemModal.value = true
  currentModalData.value = data
}
const itemModal = ref(false)
/**
 * Balance
 * Table of active hardware inventory, so rep can add to balance
 */
const { getCurrentBalanceAsTable: balanceData, getAllParts: allItemsData } = storeToRefs(
  useInventory()
)
const { updateBalance, removeItemFromBalance } = useInventory()
const { storeActive, getActiveParentTimer } = storeToRefs(useTimers())
const adjustInvetoryReasonCodes = computed(() => [
  { reason_name: 'Cycle Count', name: 'cycleCount', show: true },
  { reason_name: 'Transfer', name: 'transfer', show: true },
  { reason_name: 'Damaged', name: 'damaged', show: true },
  // { reason_name: 'autoEquipmentImport', name: 'autoEquipmentImport' }, //do not use
  { reason_name: 'Recovered at Store', name: 'recoveredAtStore', show: storeActive.value },
  { reason_name: 'Others', name: 'others', show: true },
])
const adjustInventoryHeaders = computed((): { header: string; subheaders: [string, string] } => {
  return {
    header: 'Adjust Amount',
    subheaders: [`${currentModalData.value.description}`, `${currentModalData.value.quantity}`],
  }
})
const adjustInventoryItem = async (e: any) => {
  console.log(e)
  await updateBalance({
    id: e.data.id,
    quantity: e.quantity,
    originalQuantity: e.originalQuantity,
    date: e.date,
    reason: e.reason.name,
    reason_name: e.reason.reason_name,
    comment: e.comment ?? null,
    ...(e.toRep && { toRep: e.toRep.id }),
    ...(e.reason.name == 'recoveredAtStore' && { from: getActiveParentTimer.value!.data.store_id }),
  })
}
const removeInventoryItem = async (e: any) => {
  await removeItemFromBalance({ id: e.data.id })
}

const addInvetoryReasonCodes = computed(() => [
  { reason_name: 'Cycle Count', name: 'cycleCount', show: true },
  { reason_name: 'Recovered at Store', name: 'recoveredAtStore', show: storeActive.value },
  { reason_name: 'Others', name: 'others', show: true },
])

const addInventoryHeaders = computed((): { header: string; subheaders: [string, string] } => {
  return {
    header: 'Add Inventory Item',
    subheaders: [
      `${currentModalData.value.id}/${currentModalData.value.description}`,
      `${currentModalData.value.pl_short_name}/${currentModalData.value.pl_description}`,
    ],
  }
})
const { addItemToBalance } = useInventory()

const addInventoryItem = async (e: any) => {
  //
  await addItemToBalance({
    id: e.data.id,
    description: e.data.description,
    pl_short_name: e.data.pl_short_name,
    pl_description: e.data.pl_description,
    quantity: e.quantity,
    image: e.data.image,
    reason: e.reason.name,
    reason_name: e.reason.reason_name,
    comment: e.comment,
    ...(e.toRep && { toRep: e.toRep.id }),
    ...(e.reason.name == 'recoveredAtStore' && { from: getActiveParentTimer.value!.data.store_id }),
  })
}

/**
 * Connection logic
 */
const { getStatus } = useNetwork()
</script>
<template>
  <BannerOffline data-test="pageInventory">
    You are offline, please connect in order to adjust inventory.
  </BannerOffline>
  <q-page class="q-pt-sm">
    <!-- Modal to preview inventory part -->
    <q-dialog v-model="itemModal">
      <FormInventoryView
        :header="currentModalData['description']"
        :image="currentModalData['image']"
      />
    </q-dialog>
    <q-tabs
      v-model="tab"
      class="text-grey"
      active-color="secondary"
      indicator-color="secondary"
      align="left"
      narrow-indicator
    >
      <q-tab name="balance" :label="tablesLabel('balance')" />
      <q-tab name="allItems" :label="tablesLabel('allItems')" />
    </q-tabs>
    <q-separator />
    <q-tab-panels v-model="tab" animated>
      <q-tab-panel keep-alive name="balance">
        <filtering-data-display :table-data="balanceData">
          <template #image="row">
            <RenderPartImage
              v-if="row.rowData.image"
              @click="startImageModal(row.rowData)"
              :image-data="row.rowData.image"
              image-type="thumbnail"
            />
          </template>
          <template #description="{ rowData, filterData }">
            <TableCellRender
              :data="rowData['description']"
              data-type="string"
              :filter="filterData"
            />
          </template>
          <template #pl_info="{ rowData }">
            <RenderProductLines :row-data="rowData" />
          </template>
          <template #quantity="{ rowData }">
            <span class="text-primary underline cursor-pointer" @click="startModal(rowData)">
              {{ rowData['quantity'] }}
            </span>
          </template>
        </filtering-data-display>
        <q-dialog v-if="getStatus" v-model="modal">
          <q-card>
            <FormOrderItem
              allow-zero
              :data="currentModalData"
              :render-order="['reason', 'quantity', 'users', 'comment']"
              :reason-options="adjustInvetoryReasonCodes"
              :headers="adjustInventoryHeaders"
              :labels="{ action: 'adjust', quantity: 'newQuantity' }"
              show-remove
              @action="adjustInventoryItem($event)"
              @remove="removeInventoryItem($event)"
            />
          </q-card>
        </q-dialog>
      </q-tab-panel>
      <q-tab-panel keep-alive name="allItems">
        <filtering-data-display :table-data="allItemsData">
          <template #image="row">
            <RenderPartImage
              v-if="row.rowData.image"
              @click="startImageModal(row.rowData)"
              :image-data="row.rowData.image"
              image-type="thumbnail"
            />
          </template>
          <template #pl_info="{ rowData }">
            <RenderProductLines :row-data="rowData" />
          </template>
          <template #itemRight="{ rowData }">
            <q-btn @click="startModal(rowData)" round color="secondary" icon="add" size="sm" />
          </template>
        </filtering-data-display>
        <q-dialog v-if="getStatus" v-model="modal">
          <q-card>
            <FormOrderItem
              allow-zero
              :data="currentModalData"
              :render-order="['quantity', 'reason', 'comment']"
              :reason-options="addInvetoryReasonCodes"
              :headers="addInventoryHeaders"
              :labels="{ action: 'add', quantity: 'quantity' }"
              @action="addInventoryItem"
            />
          </q-card>
        </q-dialog>
      </q-tab-panel>
    </q-tab-panels>
  </q-page>
</template>
